import { useCallback, useEffect, useRef } from "react";

/**
 * Debounce window for the single post-turn refetch. Minerva turns stream for
 * 90–135s; each streamed AGENT message resets this timer so exactly one refetch
 * fires ~2s after the turn stops streaming — never mid-stream, where it would
 * replace the streamed answer with the server's still-empty copy.
 */
export const SETTLE_REFETCH_DELAY_MS = 2000;

interface UseSettleWindowRefetchOptions {
  /**
   * When false the returned callback is a no-op: no timer is armed, so the
   * behaviour collapses to a true rollback for the flag-off (Atlas) flow.
   */
  enabled: boolean;
  /** Debounce window, in ms, before the refetch fires after the last call. */
  delayMs?: number;
}

/**
 * Debounces a single "settle-window" refetch. Each call to the returned
 * callback resets the window, so exactly one refetch fires `delayMs` after the
 * last call — never mid-stream. The pending timer is held in a ref and cleared
 * on unmount. When `enabled` is false the callback does nothing.
 *
 * Extracted from Chatbot so the debounce can be unit-tested in isolation
 * (renderHook + fake timers) without rendering the full chat surface.
 */
export const useSettleWindowRefetch = (
  refetch: () => void,
  { enabled, delayMs = SETTLE_REFETCH_DELAY_MS }: UseSettleWindowRefetchOptions,
): (() => void) => {
  // holds the single settle-window refetch timer so it can be reset on each
  // streamed message and cleared on unmount.
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Keep the latest refetch without changing the callback identity, so a new
  // query refetch reference does not re-arm or churn the timer.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const onAgentMessage = useCallback(() => {
    if (!enabled) {
      return;
    }
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      timer.current = undefined;
      refetchRef.current();
    }, delayMs);
  }, [enabled, delayMs]);

  // cancel any pending settle-window refetch when the consumer unmounts.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );

  return onAgentMessage;
};
